import { formatPct } from "@/lib/format";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useNastaveniaGlobal,
  useUpdateNastaveniaGlobal,
} from "@/api/hooks/useNastaveniaGlobal";
import type { GlobalNastavenia } from "@/api/schemas";
import { odkazNaNastavenia } from "@/components/nastavenia/kategorie";

/**
 * Karty Nastavení nad `GlobalNastavenia` — DPH, predfrez, nadmiera hrany,
 * krátenie tyčí, porez tabúľ, zvyšky a CNC stroj.
 *
 * Do KOLA 170 držala ich stav stránka a všetky boli pod sebou. Teraz sú
 * v rôznych kategóriách, takže každá karta drží svoje polia sama a číta
 * ten istý dotaz `nastavenia-global` (TanStack ho zdieľa). Každá ukladá
 * len zmenené kľúče svojím tlačidlom Uložiť, ako predtým.
 */

type Kluc = keyof GlobalNastavenia & string;

/**
 * Polia karty ako text formulára, naplnené zo servera.
 *
 * Znova sa naplnia, len keď sa zmenia uložené hodnoty **týchto** kľúčov.
 * Uloženie inej karty invaliduje celý dotaz a príde nový objekt — keby
 * sa polia plnili pri každej odpovedi, zahodila by sa rozpísaná hodnota.
 */
/** Desatinné číslo zo servera bez koncových núl — „4.40" → „4.4", „15.00" → „15". */
function bezNul(text: string): string {
  return /^-?\d+\.\d+$/.test(text) ? String(Number(text)) : text;
}

function useFormular<K extends Kluc>(
  kluce: readonly K[],
): {
  hodnoty: Record<K, string>;
  nastav: (kluc: K, hodnota: string) => void;
  server: Record<K, string> | null;
  nacitava: boolean;
} {
  const query = useNastaveniaGlobal();
  const data = query.data?.data;
  const server = useMemo(() => {
    if (!data) return null;
    return Object.fromEntries(
      kluce.map((k) => [k, data[k] != null ? bezNul(String(data[k])) : ""]),
    ) as Record<K, string>;
  }, [data, kluce]);
  const podpis = server ? JSON.stringify(server) : null;
  const [hodnoty, setHodnoty] = useState<Record<K, string>>(
    () => Object.fromEntries(kluce.map((k) => [k, ""])) as Record<K, string>,
  );
  useEffect(() => {
    if (server) setHodnoty(server);
    // `server` sa mení s každou odpoveďou; podpis len so zmenou hodnôt
  }, [podpis]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    hodnoty,
    nastav: (kluc, hodnota) => setHodnoty((prev) => ({ ...prev, [kluc]: hodnota })),
    server,
    nacitava: query.isLoading,
  };
}

/** PATCH zmenených kľúčov a jedna hláška — ten istý tvar pre každú kartu. */
function useUlozNastavenia(): {
  uloz: (zmeny: Record<string, string | number>, popis: string) => Promise<void>;
  uklada: boolean;
} {
  const update = useUpdateNastaveniaGlobal();
  async function uloz(
    zmeny: Record<string, string | number>,
    popis: string,
  ): Promise<void> {
    try {
      if (Object.keys(zmeny).length > 0) {
        await update.mutateAsync(zmeny);
      }
      toast({ title: "Uložené", description: popis });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    }
  }
  return { uloz, uklada: update.isPending };
}

/** Zmenené a vyplnené polia; `cislo` = posielať ako číslo (mm, aj s desatinami). */
function zmenene<K extends Kluc>(
  kluce: readonly K[],
  hodnoty: Record<K, string>,
  server: Record<K, string> | null,
  cislo: boolean,
): Record<string, string | number> {
  const zmeny: Record<string, string | number> = {};
  for (const k of kluce) {
    const h = hodnoty[k];
    if (h !== "" && h !== (server?.[k] ?? "")) zmeny[k] = cislo ? Number(h) : h;
  }
  return zmeny;
}

function Karta({
  nazov,
  popis,
  children,
}: {
  nazov: string;
  popis: React.ReactNode;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="text-xl">{nazov}</h2>
        <p className="text-xs text-muted-foreground">{popis}</p>
      </div>
      {children}
    </section>
  );
}

// ─── Ceny a cenníky ──────────────────────────────────────────

const DPH = ["dph_pct"] as const;

/** DPH — predvoľba pre nový výpočet. */
export function DphSection(): JSX.Element {
  const { hodnoty, nastav, server, nacitava } = useFormular(DPH);
  const { uloz } = useUlozNastavenia();
  return (
    <Karta
      nazov="DPH"
      popis={
        <>
          Sadzba, ktorú dostane <strong>nový</strong> výpočet. Uložené
          zákazky sa nemenia — ponuka musí držať sadzbu, ktorá pri nej
          platila. Sadzbu jedného výpočtu prepíšeš v „Ceny a marže“.
        </>
      }
    >
      <div className="max-w-md space-y-1">
        <Label htmlFor="dph-pct">Sadzba DPH (%)</Label>
        <Input
          id="dph-pct"
          type="number"
          min={0}
          max={100}
          step="1"
          value={hodnoty.dph_pct}
          placeholder={nacitava ? "…" : "20"}
          onChange={(e) => nastav("dph_pct", e.target.value)}
        />
      </div>
      <Button
        size="sm"
        onClick={() =>
          void uloz(
            zmenene(DPH, hodnoty, server, false),
            `DPH pre nové výpočty ${formatPct(hodnoty.dph_pct)}`,
          )
        }
      >
        Uložiť
      </Button>
    </Karta>
  );
}

const VLASTNA = ["vlastna_rezia_pct"] as const;

/** Predvolená réžia vlastnej položky mimo cenníka (KOLO 210). */
export function VlastnaPolozkaSection(): JSX.Element {
  const { hodnoty, nastav, server, nacitava } = useFormular(VLASTNA);
  const { uloz } = useUlozNastavenia();
  return (
    <Karta
      nazov="Vlastná položka mimo cenníka"
      popis={
        <>
          Réžia, s ktorou okno Vlastná položka (LED, oceľ od dodávateľa)
          začína. Položka si ju nesie — zmena tu nemení už pridané.
        </>
      }
    >
      <div className="max-w-md space-y-1">
        <Label htmlFor="vlastna-rezia-pct">Predvolená réžia (%)</Label>
        <Input
          id="vlastna-rezia-pct"
          type="number"
          min={0}
          max={500}
          step="1"
          value={hodnoty.vlastna_rezia_pct}
          placeholder={nacitava ? "…" : "20"}
          onChange={(e) => nastav("vlastna_rezia_pct", e.target.value)}
        />
      </div>
      <Button
        size="sm"
        onClick={() =>
          void uloz(
            zmenene(VLASTNA, hodnoty, server, false),
            `Réžia vlastnej položky ${formatPct(hodnoty.vlastna_rezia_pct)}`,
          )
        }
      >
        Uložiť
      </Button>
    </Karta>
  );
}

// ─── TopSolid ────────────────────────────────────────────────

const MAX_HRUBKA = ["max_hrubka_dielca_mm"] as const;

/**
 * Najväčšia hrúbka dielca z TopSolidu (KOLO 216) — hrubší dielec je chyba
 * importu. User: *„ohýbaný dielec, ktorý som zabudol zapnúť Rozvin, mal
 * hrúbku 100 mm alebo aj 300 — to nemôže byť"*.
 */
export function MaxHrubkaDielcaSection(): JSX.Element {
  const { hodnoty, nastav, server, nacitava } = useFormular(MAX_HRUBKA);
  const { uloz } = useUlozNastavenia();
  return (
    <Karta
      nazov="Najväčšia hrúbka dielca"
      popis={
        <>
          Dielec z TopSolidu hrubší ako toto je chyba v modeli — býva to
          ohýbaný dielec bez zapnutého Rozvinu (100, 300 mm). Import ho
          ukáže na opravu a materiál v tej hrúbke pustí len ako Nenahrávať.
          0 = nekontroluje sa.
        </>
      }
    >
      <div className="max-w-md space-y-1">
        <Label htmlFor="max-hrubka-dielca">Najväčšia hrúbka (mm)</Label>
        <Input
          id="max-hrubka-dielca"
          type="number"
          min={0}
          max={1000}
          step="1"
          value={hodnoty.max_hrubka_dielca_mm}
          placeholder={nacitava ? "…" : "60"}
          onChange={(e) => nastav("max_hrubka_dielca_mm", e.target.value)}
        />
      </div>
      <Button
        size="sm"
        onClick={() =>
          void uloz(
            zmenene(MAX_HRUBKA, hodnoty, server, true),
            `Najväčšia hrúbka dielca ${hodnoty.max_hrubka_dielca_mm} mm`,
          )
        }
      >
        Uložiť
      </Button>
    </Karta>
  );
}

// ─── Hrany ───────────────────────────────────────────────────

const NADMIERA_HRANY = ["hrana_nadmiera_mm"] as const;

/** Nadmiera ABS pásky na olepenú stranu — vstupuje do ceny aj súpisu hrán. */
export function NadmieraHranySection(): JSX.Element {
  const { hodnoty, nastav, server, nacitava } = useFormular(NADMIERA_HRANY);
  const { uloz } = useUlozNastavenia();
  return (
    <Karta
      nazov="Nadmiera hrany"
      popis="Spotreba ABS pásky pri olepovaní — vstupuje do ceny hrán."
    >
      <div className="max-w-md space-y-1">
        <Label htmlFor="hrana-nadmiera-mm">
          Nadmiera hrany na olepenie (mm na stranu)
        </Label>
        <Input
          id="hrana-nadmiera-mm"
          type="number"
          min={0}
          max={1000}
          step="5"
          value={hodnoty.hrana_nadmiera_mm}
          placeholder={nacitava ? "…" : "40"}
          onChange={(e) => nastav("hrana_nadmiera_mm", e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground">
          ABS páska navyše na každú olepenú stranu dielca (nábeh/dobeh
          olepičky) — dielec 500 mm spotrebuje 500 + nadmiera mm pásky.
          Rovnako cenovka z hrany a pridaná hrana dielca (otvor).
        </p>
      </div>
      <Button
        size="sm"
        onClick={() =>
          void uloz(
            zmenene(NADMIERA_HRANY, hodnoty, server, false),
            `Nadmiera hrany +${hodnoty.hrana_nadmiera_mm} mm na olepenú stranu`,
          )
        }
      >
        Uložiť
      </Button>
    </Karta>
  );
}

const PREDFREZ = ["predfrez_mm"] as const;

/** Predfrez olepovačky → odpočet hrany z prírezu (KOLO 104). */
export function PredfrezSection(): JSX.Element {
  const { hodnoty, nastav, server } = useFormular(PREDFREZ);
  const { uloz } = useUlozNastavenia();
  const predfrez = hodnoty.predfrez_mm;
  return (
    <Karta
      nazov="Odpočet hrany (predfrez)"
      popis={
        <>
          Koľko mm stiahne predfrez na olepovačke z každej olepenej strany.
          Dielec sa olepí a až potom sa reže na čisto, takže sa hrana
          z prírezu <strong>odčíta</strong>:{" "}
          <span className="font-mono">odpočet = hrúbka hrany − predfrez</span>
          . Nadmiera dielca je preto znamienková a pri olepovanom dielci
          býva záporná.
        </>
      }
    >
      <div className="flex max-w-md items-end gap-3">
        <div className="flex-1 space-y-1">
          <Label htmlFor="predfrez">Predfrez (mm)</Label>
          <Input
            id="predfrez"
            type="number"
            min={0}
            max={10}
            step="0.1"
            value={predfrez}
            onChange={(e) => nastav("predfrez_mm", e.target.value)}
          />
        </div>
        <Button
          size="sm"
          onClick={() =>
            void uloz(
              zmenene(PREDFREZ, hodnoty, server, false),
              `Predfrez ${predfrez} mm na olepenú stranu`,
            )
          }
        >
          Uložiť
        </Button>
      </div>
      {/* Pravidlo v číslach — bez tohto sa nedá uhádnuť, čo tá hodnota
          reálne spraví s prírezom (lekcia z checkboxov v KOLE 58). */}
      <div className="max-w-md rounded border bg-background-alt p-2">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Čo to spraví s hranou
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left font-normal">Hrúbka hrany</th>
              <th className="text-right font-normal">Odpočet na stranu</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {[0.5, 1, 2].map((h) => {
              const pf = parseFloat(predfrez);
              const o = Number.isFinite(pf) ? Math.max(0, h - pf) : null;
              return (
                <tr key={h}>
                  <td>{h} mm</td>
                  <td className="text-right">
                    {o == null ? "—" : o === 0 ? "0" : `−${o}`} mm
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Základnú nadmieru (napr. +5/+5 pri <code>lep_nc</code>, ktorý ide
        prvý na CNC a olepuje sa až potom) nesie <strong>výrobný postup</strong>{" "}
        — nastavíš ju vo{" "}
        <Link to={odkazNaNastavenia("vyroba")} className="text-primary underline">
          Výrobných postupoch
        </Link>
        .
      </p>
    </Karta>
  );
}

// ─── Rezanie ─────────────────────────────────────────────────

const NAREZ = ["narez_prerez_mm", "narez_orez_mm"] as const;

/** Krátenie dĺžkových (1D) — spoločné pre panel, PDF aj súpis dodávateľov. */
export function NarezovyPlanSection(): JSX.Element {
  const { hodnoty, nastav, server } = useFormular(NAREZ);
  const { uloz, uklada } = useUlozNastavenia();
  return (
    <Karta
      nazov="Nárezový plán dĺžkových materiálov"
      popis="Parametre 1D nárezového plánu (hranoly, pracovné dosky)."
    >
      <div className="grid max-w-md gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="narez-prerez-mm">Šírka pílovej drážky (mm)</Label>
          <Input
            id="narez-prerez-mm"
            type="number"
            min={0}
            max={50}
            step="1"
            value={hodnoty.narez_prerez_mm}
            onChange={(e) => nastav("narez_prerez_mm", e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            Materiál, ktorý pri rezaní odoberie pílový kotúč.
          </p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="narez-orez-mm">Orez konca (mm)</Label>
          <Input
            id="narez-orez-mm"
            type="number"
            min={0}
            max={100}
            step="1"
            value={hodnoty.narez_orez_mm}
            onChange={(e) => nastav("narez_orez_mm", e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            Na KAŽDOM konci tyče — poškodené či nezarovnané konce.
          </p>
        </div>
      </div>
      <Button
        size="sm"
        disabled={uklada}
        onClick={() =>
          void uloz(
            zmenene(NAREZ, hodnoty, server, true),
            `Krátenie dĺžkových — prerez ${hodnoty.narez_prerez_mm} mm · orez ${hodnoty.narez_orez_mm} mm`,
          )
        }
      >
        Uložiť
      </Button>
    </Karta>
  );
}

const POREZ = ["porez_prerez_mm", "porez_orez_mm", "porez_min_dorez_mm"] as const;

// Najviac dve desatinné miesta, s čiarkou aj bodkou; prázdne = nezmenené
const DESATINNE_MM = /^\d{0,3}([.,]\d{0,2})?$/;

/**
 * Pole parametra porezu s desatinami (KOLO 213). Textové s desatinnou
 * čiarkou — `type="number"` v Chromiu so slovenčinou čiarku zahodil
 * a „4,5" sa uložilo ako 45 mm (prvý pokus naživo). Hodnota formulára
 * ostáva s bodkou (`Number`), pole ukazuje čiarku.
 */
function PolePorezu({
  id,
  label,
  hodnota,
  predvolene,
  nacitava,
  napoveda,
  onZmena,
}: {
  id: string;
  label: string;
  hodnota: string;
  predvolene: string;
  nacitava: boolean;
  napoveda: string;
  onZmena: (h: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={hodnota.replace(".", ",")}
        placeholder={nacitava ? "…" : predvolene}
        onChange={(e) => {
          if (DESATINNE_MM.test(e.target.value)) onZmena(e.target.value.replace(",", "."));
        }}
      />
      <p className="text-[11px] text-muted-foreground">{napoveda}</p>
    </div>
  );
}

/**
 * Porez tabúľ (2D optimalizácia plošných materiálov) — od KOLA 213 ako
 * Cut Rite (`Standard.prm`, `S300.spm`): prerez 4,4, orez 15, dorezanie 15.
 *
 * Do KOLA 170 sa polia nenaplnili uloženými hodnotami — ukazovali len
 * šedé 4 a 10 a Uložiť nad prázdnymi poliami hlásilo „Uložené“, hoci
 * nič neodišlo.
 */
export function PorezTabulSection(): JSX.Element {
  const { hodnoty, nastav, server, nacitava } = useFormular(POREZ);
  const { uloz } = useUlozNastavenia();
  return (
    <Karta
      nazov="Porez tabúľ"
      popis="Parametre píly pre porez plošných materiálov — ako v Cut Rite (prerez 4,4, orez 15, dorezanie 15 mm). Platia pre nový porez; uložený porez ostáva s parametrami, s ktorými sa počítal, kým ho neprepočítaš (Rezanie → Prepočítať)."
    >
      <div className="grid max-w-2xl gap-3 sm:grid-cols-3">
        <PolePorezu
          id="porez-prerez-mm"
          label="Prerez píly (mm)"
          hodnota={hodnoty.porez_prerez_mm}
          predvolene="4,4"
          nacitava={nacitava}
          napoveda="Kerf medzi kusmi na tabuli (Cut Rite OPKERF)."
          onZmena={(h) => nastav("porez_prerez_mm", h)}
        />
        <PolePorezu
          id="porez-orez-mm"
          label="Orez tabule (mm na stranu)"
          hodnota={hodnoty.porez_orez_mm}
          predvolene="15"
          nacitava={nacitava}
          napoveda="Orez každej strany tabule pred porezom (poškodené hrany)."
          onZmena={(h) => nastav("porez_orez_mm", h)}
        />
        <PolePorezu
          id="porez-min-dorez-mm"
          label="Najužšie dorezanie (mm)"
          hodnota={hodnoty.porez_min_dorez_mm}
          predvolene="15"
          nacitava={nacitava}
          napoveda="Užší pás píla neodreže — odpad v páse je 0 alebo aspoň prerez + toto."
          onZmena={(h) => nastav("porez_min_dorez_mm", h)}
        />
      </div>
      <Button
        size="sm"
        onClick={() =>
          void uloz(
            zmenene(POREZ, hodnoty, server, true),
            `Porez tabúľ — prerez ${hodnoty.porez_prerez_mm} mm · orez ${hodnoty.porez_orez_mm} mm · dorezanie ${hodnoty.porez_min_dorez_mm} mm`,
          )
        }
      >
        Uložiť
      </Button>
    </Karta>
  );
}

// Dielenské pravidlá použiteľných zvyškov porezu (mm) — kľúče API
const ZVYSOK_POLIA = [
  { key: "porez_zvysok_pozdlzny_min_dlzka", label: "Pozdĺžny pás — dĺžka", def: "600" },
  { key: "porez_zvysok_pozdlzny_min_sirka", label: "Pozdĺžny pás — šírka", def: "400" },
  { key: "porez_zvysok_priecny_min_dlzka", label: "Priečny pás — dĺžka", def: "1000" },
  { key: "porez_zvysok_priecny_min_hlbka", label: "Priečny pás — hĺbka", def: "700" },
  { key: "porez_zvysok_blok_min_dlhsia", label: "Blok — dlhšia strana", def: "800" },
  { key: "porez_zvysok_blok_min_kratsia", label: "Blok — kratšia strana", def: "500" },
] as const;
const ZVYSOK_KLUCE = ZVYSOK_POLIA.map((p) => p.key);

/** Čo sa z tabule skladuje ako použiteľný zvyšok a čo je odpad v cene. */
export function ZvyskyPorezuSection(): JSX.Element {
  const { hodnoty, nastav, server, nacitava } = useFormular(ZVYSOK_KLUCE);
  const { uloz } = useUlozNastavenia();
  return (
    <Karta
      nazov="Zvyšky porezu"
      popis="Čo sa z tabule ešte skladuje ako použiteľný zvyšok a ako zvyšky vstupujú do ceny."
    >
      <div className="space-y-2">
        <Label>Použiteľné zvyšky na sklad (mm)</Label>
        <div className="grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
          {ZVYSOK_POLIA.map((p) => (
            <div key={p.key} className="space-y-1">
              <Label
                htmlFor={`zvysok-${p.key}`}
                className="text-[11px] font-normal text-muted-foreground"
              >
                {p.label}
              </Label>
              <Input
                id={`zvysok-${p.key}`}
                type="number"
                min={0}
                max={5000}
                step="10"
                value={hodnoty[p.key]}
                placeholder={nacitava ? "…" : p.def}
                onChange={(e) => nastav(p.key, e.target.value)}
              />
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Dielenské pravidlá — čo sa ešte skladuje ako zvyšok: pozdĺžny pás
          beží po vlákne (min. dĺžka × šírka), priečny naprieč vláknom (min.
          dĺžka naprieč × hĺbka po vlákne — plytší pás je odpad), blok je
          nepásový kus v ľubovoľnej orientácii. Uni materiály používajú
          pozdĺžne minimum v oboch smeroch. Menšie kusy sú odpad v cene.
        </p>
      </div>
      <Button
        size="sm"
        onClick={() =>
          void uloz(zmenene(ZVYSOK_KLUCE, hodnoty, server, true), "Zvyšky porezu uložené")
        }
      >
        Uložiť
      </Button>
    </Karta>
  );
}

// ─── Výroba a CNC ────────────────────────────────────────────

// Minimálny čistý rozmer dielca na upnutie do CNC stroja (mm) — kľúče API
const CNC_POLIA = [
  { key: "cnc_upnutie_min_dlzka_mm", label: "Min. dĺžka upnutia", def: "200", max: 3000 },
  { key: "cnc_upnutie_min_sirka_mm", label: "Min. šírka upnutia", def: "100", max: 3000 },
  { key: "cnc_zdruzenie_medzera_mm", label: "Medzera združenia", def: "10", max: 100 },
  // DAEX skript berie dva kusy; 0 = pridávať, kým pás nedosiahne minimum
  // (správanie appky z KOLA 45)
  { key: "cnc_zdruzenie_max_kusov", label: "Kusov v príreze (max)", def: "2", max: 20 },
  { key: "cnc_rotacia_max_mm", label: "Rotačný priestor (max)", def: "350", max: 3000 },
] as const;

// Názvy operácií prírezu — objavia sa v označení pre dielňu
// (napr. „nc_zdruz_rez_2x150x40"). Každá dielňa si ich volá po svojom.
const CNC_NAZVY_POLIA = [
  { key: "cnc_nazov_rez", label: "Predrez", def: "nc_rez", popis: "dielec pod minimom stroja" },
  {
    key: "cnc_nazov_zdruz_rez",
    label: "Združenie",
    def: "nc_zdruz_rez",
    popis: "viac kusov v jednom príreze",
  },
  {
    key: "cnc_nazov_rot_rez",
    label: "Rotácia",
    def: "nc_rot_rez",
    popis: "otočený o 90° kvôli čelám",
  },
  {
    key: "cnc_nazov_rot_zdruz_rez",
    label: "Rotácia + združenie",
    def: "nc_rot_zdruz_rez",
    popis: "otočený a združený",
  },
] as const;
const CNC_KLUCE = [...CNC_POLIA.map((p) => p.key), ...CNC_NAZVY_POLIA.map((p) => p.key)];

/** CNC stroj — minimum upnutia, združenie, rotácia a názvy operácií. */
export function CncStrojSection(): JSX.Element {
  const { hodnoty, nastav, server, nacitava } = useFormular(CNC_KLUCE);
  const { uloz } = useUlozNastavenia();

  function zmenyCnc(): Record<string, string | number> {
    const zmeny = zmenene(
      CNC_POLIA.map((p) => p.key),
      hodnoty,
      server,
      true,
    );
    // Názvy operácií — prázdne pole znamená „vráť default" (backend
    // prázdny reťazec odmieta, označenie bez názvu nedáva zmysel).
    for (const p of CNC_NAZVY_POLIA) {
      const hodnota = hodnoty[p.key].trim() || p.def;
      if (hodnota !== (server?.[p.key] ?? "")) zmeny[p.key] = hodnota;
    }
    return zmeny;
  }

  return (
    <Karta
      nazov="CNC stroj — minimum upnutia"
      popis="Dielec menší ako minimum stroja dostane väčší prírez (predrez) a po CNC sa oreže; úzke kusy sa režú po dvoch v jednom príreze (združenie). Vstupuje do ceny materiálu aj porezu — zmena zneplatní existujúce porezy. 0 = vypnuté."
    >
      <div className="grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
        {CNC_POLIA.map((p) => (
          <div key={p.key} className="space-y-1">
            <Label
              htmlFor={`cnc-${p.key}`}
              className="text-[11px] font-normal text-muted-foreground"
            >
              {p.label} (mm)
            </Label>
            <Input
              id={`cnc-${p.key}`}
              type="number"
              min={0}
              max={p.max}
              step="1"
              value={hodnoty[p.key]}
              placeholder={nacitava ? "…" : p.def}
              onChange={(e) => nastav(p.key, e.target.value)}
            />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Porovnáva sa čistý rozmer orientačne (dlhšia strana vs. min. dĺžka,
        kratšia vs. min. šírka) — dielec 80×300 minimu 200×50 vyhovuje
        otočený. Prírez = max(čistá, minimum) + nadmiera dielca. Medzera
        združenia = rez pílou medzi dvoma kusmi v jednom príreze.{" "}
        <b>Rotačný priestor</b> = max. dlhšia strana, pri ktorej sa dielec
        s operáciami na čelách (zas_chrb/zas_celo) otočí o 90° kvôli
        dosiahnuteľnosti; 0 = rotácia vypnutá. Platí pre CNC dielce (aktívna
        nadmiera). Do jedného prírezu ide toľko kusov, koľko ich treba na
        dosiahnutie minima stroja — <b>počet kusov v kusovníku sa tým
        nemení</b>, mení sa len počet prírezov na pílu.
      </p>

      {/* Názvy operácií — dielňa musí z papiera vidieť, čo sa robí */}
      <div className="space-y-2 border-t pt-3">
        <div>
          <h3 className="text-sm font-medium">Názvy operácií</h3>
          <p className="text-[11px] text-muted-foreground">
            Objavia sa v označení prírezu v tabuľke dielcov a v detaile —
            napr.{" "}
            <span className="font-mono">
              {hodnoty.cnc_nazov_zdruz_rez.trim() || "nc_zdruz_rez"}
              _2x150x40
            </span>{" "}
            (2 kusy 150×40 v jednom príreze). Prázdne pole = default.
          </p>
        </div>
        <div className="grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
          {CNC_NAZVY_POLIA.map((p) => (
            <div key={p.key} className="space-y-1">
              <Label
                htmlFor={`cnc-${p.key}`}
                className="text-[11px] font-normal text-muted-foreground"
                title={p.popis}
              >
                {p.label}
              </Label>
              <Input
                id={`cnc-${p.key}`}
                maxLength={40}
                className="font-mono text-xs"
                value={hodnoty[p.key]}
                placeholder={nacitava ? "…" : p.def}
                onChange={(e) => nastav(p.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <Button
        size="sm"
        onClick={() => void uloz(zmenyCnc(), "Nastavenia CNC stroja uložené")}
      >
        Uložiť
      </Button>
    </Karta>
  );
}
